import React, { useEffect, useState } from 'react';
import { X, CheckCircle, Loader2, AlertCircle, Truck, MapPin, ArrowRight, ArrowLeft, CreditCard, Copy } from 'lucide-react';
import { OrderItem, Address, Product, ShippingMethod, ShippingDetails, PaymentMethod } from '../types';
import { createOrder } from '../services/orderService';
import { LOCAL_GTA_RATE } from '../services/shippingService';
import {
  COUNTRIES,
  regionsFor,
  computeAge,
  MIN_AGE,
  isRegionRestricted,
  getRegionNotice,
  LOCAL_LAW_NOTICE,
} from '../lib/compliance';
import { CARD_PAYMENTS_ENABLED, PAYMENT_MODE, startCardPayment } from '../lib/payments';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartCount: number;
  subtotal: number;
  businessDetails: { name: string; taxId: string } | null;
  cartItems: OrderItem[];
  products: Product[];
  isVerified?: boolean;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  cartCount,
  subtotal,
  businessDetails,
  cartItems,
  isVerified = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [error, setError] = useState('');
  const [checkoutStep, setCheckoutStep] = useState<1 | 2>(1);
  const [isCopied, setIsCopied] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ageAttested, setAgeAttested] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('etransfer');

  const [shippingAddress, setShippingAddress] = useState<Address>({
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
  });

  const [shippingMethod, setShippingMethod] = useState<ShippingMethod | null>(null);
  const shippingCost = shippingMethod === 'local_gta' ? LOCAL_GTA_RATE : 0;
  const total = subtotal + shippingCost;

  // Retail compliance gateways
  const buyerAge = computeAge(dateOfBirth);
  const regionNotice = getRegionNotice(shippingAddress.country, shippingAddress.province);
  const regionBlocked = isRegionRestricted(shippingAddress.country, shippingAddress.province);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCopyEmail = () => {
    navigator.clipboard
      .writeText('payments@themacheteman.ca')
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch((err) => console.error('Failed to copy:', err));
  };

  const handleContinueToPayment = () => {
    if (!isVerified && (!customerName || !customerEmail)) {
      setError('Please enter your name and email so we can confirm your order.');
      return;
    }
    if (!isVerified && !dateOfBirth) {
      setError('Please enter your date of birth.');
      return;
    }
    if (!isVerified && (isNaN(buyerAge) || buyerAge < MIN_AGE)) {
      setError(`You must be at least ${MIN_AGE} years old to purchase.`);
      return;
    }
    if (
      !shippingAddress.street ||
      !shippingAddress.city ||
      !shippingAddress.province ||
      !shippingAddress.postalCode
    ) {
      setError('Please complete all shipping address fields.');
      return;
    }
    if (regionBlocked) {
      setError(`We're sorry — we can't currently ship to ${shippingAddress.province}. Please contact us.`);
      return;
    }
    if (!shippingMethod) {
      setError('Please select a shipping method.');
      return;
    }
    setError('');
    setCheckoutStep(2);
  };

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const shippingDetails: ShippingDetails = { method: shippingMethod!, amount: shippingCost };
      const orderNum = await createOrder({
        retailerName: isVerified
          ? businessDetails?.name || 'Wholesale account'
          : customerName || 'Retail customer',
        orderType: isVerified ? 'wholesale' : 'retail',
        customerEmail: !isVerified ? customerEmail : undefined,
        customerPhone: !isVerified ? customerPhone : undefined,
        ageVerified: !isVerified ? ageAttested && buyerAge >= MIN_AGE : undefined,
        dateOfBirth: !isVerified ? dateOfBirth : undefined,
        paymentMethod,
        items: cartItems,
        subtotal,
        shipping: shippingCost,
        shippingAddress,
        shippingDetails,
      });

      if (paymentMethod === 'card' && CARD_PAYMENTS_ENABLED) {
        const pay = await startCardPayment({
          orderNumber: orderNum,
          amount: total,
          email: !isVerified ? customerEmail : undefined,
        });
        if (pay.success && pay.redirectUrl) {
          window.location.href = pay.redirectUrl;
          return;
        }
        throw new Error(pay.error || 'Card payment could not be started.');
      }

      setOrderNumber(orderNum);
      setIsSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setOrderNumber('');
    setError('');
    setShippingAddress({ street: '', city: '', province: '', postalCode: '', country: 'Canada' });
    setShippingMethod(null);
    setCheckoutStep(1);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setDateOfBirth('');
    setAgeAttested(false);
    setPaymentMethod('etransfer');
    onClose();
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full p-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Slide-in panel */}
      <div className="relative w-full max-w-md h-full bg-paper-bright shadow-float flex flex-col animate-in slide-in-from-right duration-400 ease-editorial">

        {/* Header */}
        <header className="px-6 py-5 bg-forge text-paper flex justify-between items-start gap-4 relative">
          <div>
            <p className="font-mono text-[10px] tracking-micro uppercase text-ember-soft mb-1.5">
              {isSubmitted
                ? '— Confirmation'
                : checkoutStep === 1
                ? `— ${isVerified ? 'Order request' : 'Your order'} · Step 1 of 2`
                : `— Payment · Step 2 of 2`}
            </p>
            <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight leading-tight">
              {isSubmitted ? 'Order submitted.' : checkoutStep === 1 ? 'Your order.' : 'Payment.'}
            </h2>
            <p className="text-xs text-paper/55 mt-1.5">
              {businessDetails?.name || (isVerified ? 'Wholesale account' : 'Retail order')}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </header>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-7">

          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-olive/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-7 h-7 text-olive" strokeWidth={1.5} />
              </div>
              <h3 className="font-editorial italic text-2xl text-ink-950 mb-2">Order submitted.</h3>
              <p className="text-sm text-ink-700 mb-8 max-w-prose mx-auto">
                Your order has been received and is awaiting payment.
              </p>

              <div className="bg-paper border border-ink-200 p-5 mb-6 text-left">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                  — Order number
                </p>
                <p className="font-mono text-2xl text-ember">{orderNumber}</p>
              </div>

              <div className="bg-brass/10 border-l-2 border-brass p-4 text-left">
                <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3">
                  — Next steps
                </p>
                <ol className="text-sm text-ink-900 space-y-2.5 leading-relaxed">
                  <li>
                    <span className="font-mono text-ink-500 mr-2">01</span>
                    Send an Interac e-Transfer to{' '}
                    <span className="font-medium text-ink-950">payments@themacheteman.ca</span>
                  </li>
                  <li>
                    <span className="font-mono text-ink-500 mr-2">02</span>
                    Include order number{' '}
                    <span className="font-mono text-ember">{orderNumber}</span> in the memo
                  </li>
                  <li>
                    <span className="font-mono text-ink-500 mr-2">03</span>
                    We'll process your order once payment is verified
                  </li>
                </ol>
              </div>
            </div>
          ) : checkoutStep === 1 ? (
            <>
              {error && (
                <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-ember-deep text-sm">{error}</p>
                </div>
              )}

              {/* Items */}
              <section className="mb-8">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4 pb-3 border-b border-ink-200">
                  — Item summary
                </p>
                {cartItems.length > 0 ? (
                  <ul className="space-y-4">
                    {cartItems.map((item, idx) => (
                      <li key={idx} className="flex justify-between items-start text-sm">
                        <div className="flex-1 pr-4">
                          <p className="text-ink-950 font-medium leading-tight">{item.name}</p>
                          <p className="font-mono text-[10px] tracking-wider text-ink-500 mt-1">
                            {item.sku}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm text-ink-900">× {item.quantity}</p>
                          <p className="font-mono text-xs text-ink-500 mt-1">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-ink-500 text-sm italic">No items selected.</p>
                )}
              </section>

              {/* Totals */}
              <section className="bg-paper border border-ink-200 p-5 mb-8 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-700">Total items</span>
                  <span className="font-mono text-ink-900">{cartCount} {cartCount === 1 ? 'unit' : 'units'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-700">Subtotal</span>
                  <span className="font-mono text-ink-900">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-700">
                    Shipping{shippingMethod === 'local_gta' && ' · Local GTA'}{shippingMethod === 'pickup' && ' · Warehouse pickup'}
                  </span>
                  <span className="font-mono text-ink-900">
                    {shippingMethod ? `$${shippingCost.toFixed(2)}` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-baseline border-t border-ink-200 pt-3 mt-3">
                  <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                    Est. total · CAD
                  </span>
                  <span className="font-editorial italic text-2xl text-ink-950">
                    ${total.toFixed(2)}
                  </span>
                </div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 pt-1">
                  Taxes calculated on the final invoice
                </p>
              </section>

              {/* Your details — retail customers have no account on file */}
              {!isVerified && (
                <section className="mb-8">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4 pb-3 border-b border-ink-200">
                    — Your details
                  </p>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Full name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className={inputClass}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="email"
                        placeholder="Email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        className={inputClass}
                      />
                      <input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                        Date of birth · {MIN_AGE}+ only
                      </label>
                      <input
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        className={inputClass}
                      />
                      {dateOfBirth && !isNaN(buyerAge) && buyerAge < MIN_AGE && (
                        <p className="mt-2 text-xs text-ember-deep">
                          You must be at least {MIN_AGE} years old to purchase.
                        </p>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* Shipping Address */}
              <section className="mb-8">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4 pb-3 border-b border-ink-200">
                  — Shipping address
                </p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Street address"
                    value={shippingAddress.street}
                    onChange={(e) => setShippingAddress((p) => ({ ...p, street: e.target.value }))}
                    className={inputClass}
                  />
                  <select
                    value={shippingAddress.country}
                    onChange={(e) =>
                      setShippingAddress((p) => ({ ...p, country: e.target.value, province: '' }))
                    }
                    className={inputClass}
                    aria-label="Country"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="City"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress((p) => ({ ...p, city: e.target.value }))}
                      className={inputClass}
                    />
                    <select
                      value={shippingAddress.province}
                      onChange={(e) => setShippingAddress((p) => ({ ...p, province: e.target.value }))}
                      className={inputClass}
                      aria-label={shippingAddress.country === 'United States' ? 'State' : 'Province'}
                    >
                      <option value="" disabled>
                        {shippingAddress.country === 'United States' ? 'State' : 'Province / Territory'}
                      </option>
                      {regionsFor(shippingAddress.country).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    placeholder="Postal / ZIP code"
                    value={shippingAddress.postalCode}
                    onChange={(e) => setShippingAddress((p) => ({ ...p, postalCode: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </section>

              {/* Regional compliance notices (retail) */}
              {!isVerified && (regionBlocked || regionNotice) && (
                <section className="mb-8 space-y-3">
                  {regionBlocked && (
                    <div className="bg-ember-soft/25 border-l-2 border-ember p-3 text-xs text-ember-deep leading-relaxed">
                      We're not currently able to ship to {shippingAddress.province}. Please contact us to arrange your order.
                    </div>
                  )}
                  {regionNotice && (
                    <div className="bg-brass/10 border-l-2 border-brass p-3 text-xs text-ink-900 leading-relaxed">
                      <span className="font-medium text-ink-950">{shippingAddress.province}:</span> {regionNotice}
                    </div>
                  )}
                </section>
              )}

              {/* Shipping Method */}
              <section className="mb-2">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4 pb-3 border-b border-ink-200">
                  — Shipping method
                </p>
                <div className="space-y-3">
                  {/* Warehouse pickup — live since the Oct 2026 move-in */}
                  <label
                    className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors duration-150 ease-editorial ${
                      shippingMethod === 'pickup'
                        ? 'border-ember bg-ember/5'
                        : 'border-ink-200 hover:border-ink-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shippingMethod"
                      checked={shippingMethod === 'pickup'}
                      onChange={() => setShippingMethod('pickup')}
                      className="mt-1 accent-ember"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="inline-flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-ember" strokeWidth={1.5} />
                          <span className="text-sm font-medium text-ink-950">Warehouse pickup · Scarborough</span>
                        </div>
                        <span className="font-mono text-sm text-olive">FREE</span>
                      </div>
                      <p className="text-xs text-ink-700 mt-1.5">
                        345 Nugget Avenue, Unit 17 — by appointment; we confirm your pickup time on
                        WhatsApp once payment clears. Government photo ID required at handoff (18+).
                      </p>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors duration-150 ease-editorial ${
                      shippingMethod === 'local_gta'
                        ? 'border-ember bg-ember/5'
                        : 'border-ink-200 hover:border-ink-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shippingMethod"
                      checked={shippingMethod === 'local_gta'}
                      onChange={() => setShippingMethod('local_gta')}
                      className="mt-1 accent-ember"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="inline-flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-ember" strokeWidth={1.5} />
                          <span className="text-sm font-medium text-ink-950">Local GTA delivery</span>
                        </div>
                        <span className="font-mono text-sm text-ember">${LOCAL_GTA_RATE.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-ink-700 mt-1.5">
                        Manual delivery within Greater Toronto Area
                      </p>
                    </div>
                  </label>

                  <div className="border border-ink-200">
                    <div className="flex items-start gap-3 p-4">
                      <input type="radio" name="shippingMethod" disabled className="mt-1" />
                      <div className="flex-1">
                        <div className="inline-flex items-center gap-2">
                          <Truck className="w-3.5 h-3.5 text-ink-500" strokeWidth={1.5} />
                          <span className="text-sm font-medium text-ink-700">Ship to me · Canada-wide</span>
                        </div>
                        <p className="text-xs text-ink-500 mt-1.5">
                          Arranged personally while we finish our carrier rollout —{' '}
                          <a
                            href="https://wa.me/14166840010?text=Hi%2C%20I%27d%20like%20to%20arrange%20shipping%20for%20a%20machete%20order%20outside%20the%20GTA."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ember underline underline-offset-2 hover:text-ember-deep"
                          >
                            message us on WhatsApp
                          </a>{' '}
                          for a quote.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <>
              {error && (
                <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-ember-deep text-sm">{error}</p>
                </div>
              )}

              {/* Summary */}
              <section className="bg-paper border border-ink-200 p-5 mb-6">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4">
                  — Order summary
                </p>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink-700">Items ({cartCount} {cartCount === 1 ? 'unit' : 'units'})</span>
                    <span className="font-mono text-ink-900">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-700">
                      {shippingMethod === 'pickup' ? 'Warehouse pickup · Scarborough' : 'Shipping · Local GTA'}
                    </span>
                    <span className="font-mono text-ink-900">${shippingCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-baseline border-t border-ink-200 pt-3 mt-3">
                    <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                      Total · CAD
                    </span>
                    <span className="font-editorial italic text-2xl text-ember">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 pt-1">
                    Taxes calculated on the final invoice
                  </p>
                </div>
              </section>

              {/* Shipping To */}
              <section className="bg-paper border border-ink-200 p-5 mb-6">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-3">
                  — Shipping to
                </p>
                <p className="text-sm text-ink-900 leading-[1.65]">
                  {shippingAddress.street}
                  <br />
                  {shippingAddress.city}, {shippingAddress.province} {shippingAddress.postalCode}
                  <br />
                  {shippingAddress.country}
                </p>
              </section>

              {/* Payment method selector — only shown when card is enabled */}
              {CARD_PAYMENTS_ENABLED && (
                <section className="mb-6">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-3">
                    — Payment method
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('etransfer')}
                      className={`p-3 text-sm border text-left transition-colors duration-150 ${
                        paymentMethod === 'etransfer'
                          ? 'border-ember bg-ember/5 text-ink-950'
                          : 'border-ink-200 text-ink-700 hover:border-ink-300'
                      }`}
                    >
                      Interac e-Transfer
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-3 text-sm border text-left transition-colors duration-150 ${
                        paymentMethod === 'card'
                          ? 'border-ember bg-ember/5 text-ink-950'
                          : 'border-ink-200 text-ink-700 hover:border-ink-300'
                      }`}
                    >
                      Credit / debit card
                    </button>
                  </div>
                </section>
              )}

              {/* Payment — e-Transfer */}
              {paymentMethod === 'etransfer' && (
              <section className="border border-ember">
                <div className="bg-ember text-paper-warm px-4 py-3 inline-flex items-center gap-2 w-full">
                  <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-mono text-[11px] tracking-micro uppercase">
                    Payment instructions
                  </span>
                </div>
                <div className="p-5 space-y-5">
                  <p className="text-sm text-ink-900 leading-relaxed">
                    To complete your order, please send an{' '}
                    <span className="font-medium text-ink-950">Interac e-Transfer</span> for the total.
                  </p>

                  <div className="bg-paper border border-ink-200 p-4 space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                        Recipient
                      </span>
                      <span className="text-ink-950">The Machete Man</span>
                    </div>
                    <div className="flex justify-between items-center gap-3">
                      <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                        Email
                      </span>
                      <button
                        onClick={handleCopyEmail}
                        className={`font-mono text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 border transition-colors duration-150 ${
                          isCopied
                            ? 'bg-olive/10 text-olive border-olive/30'
                            : 'bg-paper-bright text-ember border-ember/30 hover:bg-ember/5'
                        }`}
                      >
                        <Copy className="w-3 h-3" strokeWidth={1.5} />
                        {isCopied ? 'Copied' : 'payments@themacheteman.ca'}
                      </button>
                    </div>
                  </div>

                  <div className="bg-brass/10 border-l-2 border-brass p-3">
                    <p className="text-xs text-ink-900 leading-relaxed">
                      <span className="font-medium text-ink-950">Important:</span> include your{' '}
                      <span className="font-medium text-ink-950">order number</span> (provided after
                      submission) in the transfer memo. Your order will be processed once payment is
                      verified.
                    </p>
                  </div>
                </div>
              </section>
              )}

              {/* Payment — Card (hosted redirect or embedded fields) */}
              {paymentMethod === 'card' && (
                <section className="border border-ember">
                  <div className="bg-ember text-paper-warm px-4 py-3 inline-flex items-center gap-2 w-full">
                    <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-mono text-[11px] tracking-micro uppercase">
                      Card payment
                    </span>
                  </div>
                  <div className="p-5 space-y-4">
                    {PAYMENT_MODE === 'hosted' ? (
                      <p className="text-sm text-ink-900 leading-relaxed">
                        You'll be securely redirected to our payment provider to complete your card
                        payment, then returned here.
                      </p>
                    ) : (
                      <div id="card-payment-fields" className="min-h-[120px] border border-ink-200 p-4 text-sm text-ink-500">
                        Secure card fields load here.
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Compliance acknowledgment — required for retail checkout */}
              {!isVerified && (
                <section className="mt-6 space-y-3">
                  <div className="bg-paper border border-ink-200 p-3 text-xs text-ink-700 leading-relaxed">
                    {LOCAL_LAW_NOTICE}
                  </div>
                  <label className="flex items-start gap-3 p-4 bg-paper border border-ink-200 cursor-pointer hover:border-ink-300 transition-colors duration-150">
                    <input
                      type="checkbox"
                      checked={ageAttested}
                      onChange={(e) => setAgeAttested(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-ember flex-shrink-0"
                    />
                    <span className="text-xs text-ink-900 leading-relaxed">
                      I confirm the date of birth I provided is accurate, I agree to present valid
                      government-issued <span className="font-medium text-ink-950">photo ID on delivery or pickup</span>,
                      and I will use this product lawfully and follow the rules where I live.
                    </span>
                  </label>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        <footer className="p-5 lg:p-6 border-t border-ink-200 bg-paper">
          {isSubmitted ? (
            <button
              className="w-full py-3.5 bg-forge hover:bg-forge-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial"
              onClick={handleClose}
            >
              Close
            </button>
          ) : checkoutStep === 1 ? (
            <button
              className="w-full py-3.5 bg-ember hover:bg-ember-deep disabled:opacity-50 disabled:cursor-not-allowed text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
              onClick={handleContinueToPayment}
              disabled={cartItems.length === 0}
            >
              <span>Continue to payment</span>
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          ) : (
            <div className="space-y-3">
              <button
                className="w-full py-3.5 bg-ember hover:bg-ember-deep disabled:opacity-50 disabled:cursor-not-allowed text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
                onClick={handleSubmit}
                disabled={isSubmitting || (!isVerified && !ageAttested)}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                    <span>{isVerified ? 'Submit purchase order' : 'Place order'}</span>
                  </>
                )}
              </button>
              <button
                className="w-full py-2 text-sm text-ink-700 hover:text-ember transition-colors duration-150 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                onClick={() => setCheckoutStep(1)}
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>Back to shipping</span>
              </button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
};

export default CheckoutModal;
